import React, { useState, useEffect, useCallback } from 'react';
import { useT } from '../i18n';
import {
  listSupportMessages, setSupportStatus,
  getSupportAdminUid, setSupportAdminUid,
} from '../firebase/supportService';
import { useAuth } from '../context/AuthContext';
import './AdminSupportInbox.css';

// ── SUPPORT INBOX ────────────────────────────────────────────────────────────
// The admin-side of the "Contact us" widget. Its own component rather than more
// lines in AdminDashboard, because it is the only block on that page that is
// not a number — it fetches a list, has per-row state and mutates documents,
// none of which the analytics sections do.
//
// ADMIN-ONLY BY THE RULES, NOT BY THIS COMPONENT. `listSupportMessages` is
// refused for anyone without the claim, so a non-admin reaching this by hand
// gets an error rather than data. The page is behind AdminRoute as well; that
// guard is the convenience, the rules are the control.
//
// READ ON DEMAND, not live. A support inbox does not need a realtime listener
// — it is read when someone opens the page and refreshed when they ask — and a
// snapshot listener here would hold an open query for the life of the tab.

function when(ts) {
  // Firestore timestamp → local string. `createdAt` is a server sentinel, so a
  // document read back immediately after a write can briefly have null here.
  const d = ts?.toDate?.();
  if (!d) return '—';
  return d.toLocaleString();
}

export default function AdminSupportInbox() {
  const { t } = useT();
  const { user } = useAuth();
  // Which account receives "Contact admin" conversations. Shown here because
  // this is the page whose reader is allowed to change it, and because a
  // button that silently does not exist is worse than one that says why.
  const [supportUid, setSupportUid] = useState(null);   // null = still loading
  const [claiming, setClaiming] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await listSupportMessages(50));
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getSupportAdminUid({ force: true }).then(setSupportUid); }, []);

  const claim = useCallback(async () => {
    if (!user?.uid) return;
    setClaiming(true);
    try {
      await setSupportAdminUid(user.uid);
      setSupportUid(user.uid);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setClaiming(false);
    }
  }, [user]);

  const toggle = useCallback(async (row) => {
    const next = row.status === 'handled' ? 'new' : 'handled';
    setBusyId(row.id);
    try {
      await setSupportStatus(row.id, next);
      // Patched locally rather than refetched: the change is one field and
      // known, and a refetch would reorder nothing but cost a query.
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, status: next } : r)));
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setBusyId('');
    }
  }, []);

  const unread = rows.filter(r => r.status !== 'handled').length;

  return (
    <section className="adm-card asi">
      <header className="asi-head">
        <h3 className="adm-h3">
          {t('adm_support')}
          {unread > 0 && <span className="asi-badge">{unread}</span>}
        </h3>
        <button type="button" className="asi-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : '⟳'}
        </button>
      </header>

      {/* ── WHERE "CONTACT ADMIN" GOES ─────────────────────────────────────
          The button in a signed-in user's Messages needs a uid to open a
          conversation with, and admin is a claim rather than a queryable
          field, so the account has to be named. This is where it gets named.

          SHOWN EVEN WHEN IT IS ALREADY SET, because "which account receives
          support" is worth being able to check without reading the database —
          and worth being able to move, when whoever is on duty changes. */}
      {supportUid !== null && (
        <div className={`asi-cfg${supportUid ? '' : ' asi-cfg--unset'}`}>
          <span className="asi-cfg-text">
            {supportUid === user?.uid
              ? t('adm_support_target_you')
              : supportUid
                ? t('adm_support_target_other').replace('{uid}', supportUid)
                : t('adm_support_target_none')}
          </span>
          {supportUid !== user?.uid && (
            <button type="button" className="asi-mark" onClick={claim} disabled={claiming}>
              {claiming ? '…' : t('adm_support_claim')}
            </button>
          )}
        </div>
      )}

      {error && <p className="asi-error" role="alert">{error}</p>}

      {!loading && !rows.length && !error && (
        <p className="asi-empty">{t('adm_support_none')}</p>
      )}

      <ul className="asi-list">
        {rows.map(r => (
          <li key={r.id} className={`asi-row${r.status === 'handled' ? ' asi-row--done' : ''}`}>
            <div className="asi-meta">
              <span className={`asi-status asi-status--${r.status === 'handled' ? 'done' : 'new'}`}>
                {r.status === 'handled' ? t('adm_support_handled') : t('adm_support_new')}
              </span>
              <span className="asi-when">{when(r.createdAt)}</span>
              {r.page && <span className="asi-page">{r.page}</span>}
            </div>

            {/* The message, as written. `white-space: pre-wrap` in the CSS, so
                the line breaks somebody typed survive — a support message is
                often a list of steps. */}
            <p className="asi-msg">{r.message}</p>

            <div className="asi-foot">
              <span className="asi-from">
                {r.name || (r.fromUid ? r.fromUid : t('adm_support_signedout'))}
                {/* Whatever they gave to be reached on. A tel:/mailto: would
                    need to guess which it is, so it is shown as text and can
                    be copied. */}
                {r.contact && <span className="asi-contact">{r.contact}</span>}
              </span>
              <button
                type="button"
                className="asi-mark"
                onClick={() => toggle(r)}
                disabled={busyId === r.id}
              >
                {r.status === 'handled' ? t('adm_support_reopen') : t('adm_support_mark')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
